import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import markerUrl from "../../images/marker.svg"
import { distanceText, feetToMeters, metersToFeet } from "../utils/distance"
import env from "../utils/env"
import { useResize } from "stimulus-use"

export default class extends Controller {
  static values = {
    interactive: { type: Boolean, default: true },
    disabled: Boolean,
    center: Array,
    radius: Number
  }

  connect() {
    useResize(this)

    this.create()
  }

  disconnect() {
    this.destroy()
  }

  // fixes broken map when it's been rendered hidden
  resize({ width }) {
    if (width === 0) return

    this.map.invalidateSize()
    this.updateView()
  }

  disabledValueChanged() {
    this.element.toggleAttribute("inert", this.disabledValue)
    this.update()
  }

  centerValueChanged() {
    this.update()
  }

  radiusValueChanged() {
    this.update()
  }

  create() {
    this.render()
    this.update()
  }

  destroy() {
    this.removeLayers()
    this.map?.remove()
    this.map = null
  }

  render() {
    this.map = L.map(this.element, {
      zoomControl: this.interactiveValue,
      scrollWheelZoom: this.interactiveValue,
      doubleClickZoom: this.interactiveValue,
      dragging: this.interactiveValue,
    })

    if (env !== "test") {
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; <a href=\"http://www.openstreetmap.org/copyright\">OpenStreetMap</a>"
      }).addTo(this.map)
    }

    this.map.fitWorld()
  }

  update() {
    if (!this.map) return

    if (this.hasCenterValue) {
      this.updateCenter()
      this.updateCircle()
      this.updateRadius()
      this.updateView()
    } else {
      this.removeLayers()
    }
  }

  removeLayers() {
    this.removeCenter()
    this.removeCircle()
    this.removeRadius()
  }

  updateView() {
    if (this.center && !this.radiusDragging) {
      this.map.setView(this.centerCoordinates, this.zoom())
    }
  }

  updateCircle() {
    if (this.circle) {
      this.circle.setLatLng(this.centerCoordinates)
      this.circle.setRadius(this.radius)
    } else {
      this.createCircle()
    }

    this.circle.getElement().classList.toggle("hidden", this.disabledValue)
  }

  updateRadius() {
    if (this.radiusLine && this.radiusMarker) {
      this.radiusLine.setLatLngs([this.centerCoordinates, this.circlePoint()])
      this.radiusMarker.setLatLng(this.circlePoint())
      this.radiusLabel.setLatLng(this.radiusLabelCoordinates())
      this.radiusLabel.setIcon(this.radiusLabelIcon())
    } else {
      this.createRadius()
    }

    this.radiusLine.getElement().classList.toggle("hidden", this.disabledValue)
    this.radiusMarker.getElement().classList.toggle("hidden", this.disabledValue)
    this.radiusLabel.getElement().classList.toggle("hidden", this.disabledValue)
  }

  updateCenter() {
    if (this.center) {
      this.center.setLatLng(this.centerValue)
    } else {
      this.createCenter()
    }
  }

  createCenter() {
    const markerIcon = L.icon({
      iconUrl: markerUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    })

    this.center = L.marker(this.centerValue, {
      icon: markerIcon,
      interactive: this.interactiveValue
    }).addTo(this.map)
  }

  createCircle() {
    this.circle = L.circle(this.centerCoordinates, {
      color: "var(--color-primary-base)",
      fillColor: "var(--color-primary-base)",
      fillOpacity: 0.2,
      radius: this.radius,
      interactive: this.interactiveValue
    }).addTo(this.map)
  }

  createRadius() {
    this.createRadiusLine()
    this.createRadiusMarker()
    this.createRadiusLabel()
  }

  createRadiusLine() {
    this.radiusLine = L.polyline([this.centerCoordinates, this.circlePoint()], {
      color: "var(--color-primary-base)",
      weight: 2,
      dashArray: "4, 5",
      interactive: this.interactiveValue
    }).addTo(this.map)
  }

  createRadiusMarker() {
    this.radiusMarker = L.marker(this.circlePoint(), {
      draggable: this.interactiveValue,
      interactive: this.interactiveValue,
      icon: L.divIcon({
        className: "rounded-full border-2 border-white shadow-xs bg-primary-base",
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      })
    }).addTo(this.map)

    this.radiusMarker.on("dragstart", () => this.radiusDragging = true)
    this.radiusMarker.on("drag", () => {
      // Constrain edge marker to horizontal movement only
      this.radiusMarker.setLatLng([this.centerCoordinates.lat, this.edgeCoordinates.lng])

      // Calculate new radius based on horizontal distance
      this.radiusValue = metersToFeet(this.centerCoordinates.distanceTo(this.edgeCoordinates))

      this.dispatch("radius-changed", { detail: { radius: this.radiusValue } })
    })
    this.radiusMarker.on("dragend", () => this.radiusDragging = false)
  }

  createRadiusLabel() {
    this.radiusLabel = L.marker(this.radiusLabelCoordinates(), {
      icon: this.radiusLabelIcon(),
      interactive: this.interactiveValue
    }).addTo(this.map)
  }

  radiusLabelIcon() {
    return L.divIcon({
      className: "text-primary-base text-center font-semibold font-inter whitespace-nowrap",
      html: distanceText(this.radiusValue),
      iconSize: [100, 20], // Adjust width/height as needed
      iconAnchor: [50, 10] // Center the text,
    })
  }

  radiusLabelCoordinates() {
    // Calculate midpoint
    const midLat = (this.centerCoordinates.lat + this.edgeCoordinates.lat) / 2
    const midLng = (this.centerCoordinates.lng + this.edgeCoordinates.lng) / 2

    // Offset the text slightly above the line
    const textOffset = 0.001 // Adjust this value to move text higher/lower

    return [midLat + textOffset, midLng]
  }

  removeCenter() {
    this.center?.remove()
    this.center = null
  }

  removeCircle() {
    this.circle?.remove()
    this.circle = null
  }

  removeRadius() {
    this.radiusLine?.remove()
    this.radiusLine = null

    this.radiusLabel?.remove()
    this.radiusLabel = null

    this.radiusMarker?.off()
    this.radiusMarker?.remove()
    this.radiusMarker = null
  }

  circlePoint() {
    // right-hand intersection point of horizontal line at the center level
    return [this.centerCoordinates.lat, this.circle.getBounds().getEast()]
  }

  zoom() {
    if (this.circle && this.map.hasLayer(this.circle)) {
      return this.map.getBoundsZoom(this.circle.getBounds())
    } else {
      return this.map.getZoom()
    }
  }

  get centerCoordinates() {
    return this.center.getLatLng()
  }

  get edgeCoordinates() {
    return this.radiusMarker.getLatLng()
  }

  get radius() {
    return feetToMeters(this.radiusValue)
  }
}
